import { ref, computed, unref, watch, watchEffect } from '@vue/composition-api'
import GetTicketByIdModel from '@/models/responses/ticket/GetTicketByIdModel'
import TiposDeSecoesEnum from '@/models/entities/formulario/secao/TiposDeSecoesEnum'
import TicketCategoriaStatusEnum from '@/models/entities/ticket/TicketCategoriaStatusEnum'
import TiposDeCanaisEnum from '@/models/entities/canal/TiposDeCanaisEnum'
import ChatBotProvedorComunicacaoEnum from '@/models/entities/canal/ChatBotProvedorComunicacaoEnum'
import TicketService from '@/services/modules/TicketService'
import DocumentoService from '@/services/modules/DocumentoService'
import useAtendimentoStore from '@/store/atendimento'
import { EventBus } from '@/utils/EventBus'
import { areObjectPropertiesEqual } from '@/utils/objects'

export default function useAtendimentoTicket(ticketId) {
  const ticketData = ref(new GetTicketByIdModel())
  const ticketDataOriginal = ref(new GetTicketByIdModel())
  const imagensEDocumentos = ref([])
  const formulariosRespostas = ref([])

  const isGetTicketLoading = ref(false)
  const isGetSecaoDefaultDadosDoSolicitanteLoading = ref(false)
  const isGetSecoesFormulariosLoading = ref(false)
  const isGetSecaoDefaultMensagensLoading = ref(false)
  const isGetLoading = computed(() => isGetTicketLoading.value || isGetSecaoDefaultDadosDoSolicitanteLoading.value || isGetSecaoDefaultMensagensLoading.value || isGetSecoesFormulariosLoading.value)

  const secaoDefaultDadosDoSolicitante = ref({})
  const secaoDefaultMensagens = ref({})
  const secoesPersonalizadas = ref([])

  const ticketEstaFinalizado = computed(() => ticketData.value.categoriaStatusId === TicketCategoriaStatusEnum.Finalizado)
  const ticketEstaResolvido = computed(() => ticketData.value.categoriaStatusId === TicketCategoriaStatusEnum.Resolvido)
  const podeGravarAudio = computed(() => [TiposDeCanaisEnum.WhatsApp, TiposDeCanaisEnum.Bot].includes(ticketData.value.canalTipoId))
  const podeResponderMensagem = computed(() => ticketData.value.canalTipoId === TiposDeCanaisEnum.WhatsApp || (ticketData.value.canalTipoId === TiposDeCanaisEnum.Bot && ticketData.value.chatBotProvedorComunicacaoId === ChatBotProvedorComunicacaoEnum.WhatsApp))

  const expansionPanelsQueDevemAbrirPorDefault = ref([0])

  const { setIsLoading, setTicketSelecionadoFoiAlterado } = useAtendimentoStore()
  watchEffect(() => { setIsLoading(isGetLoading.value) })

  async function getTicketById() {
    if (isGetTicketLoading.value) return

    try {
      isGetTicketLoading.value = true
      const data = await TicketService.getTicketById(unref(ticketId))
      ticketData.value = data
      ticketDataOriginal.value = JSON.parse(JSON.stringify(data))
    } finally {
      setTimeout(() => { isGetTicketLoading.value = false }, 1000)
    }
  }

  async function getTicketMensagensAnexos() {
    const contentTypeRegex = '(?<type>[a-z0-9]+)/(?<subtype>[a-z0-9]+)'

    const anexos = await DocumentoService.getTicketMensagensAnexos(unref(ticketId))

    imagensEDocumentos.value = anexos.filter(it => it.contentType.match(contentTypeRegex).groups.type !== 'audio')
  }

  async function getSecaoDefaultDadosDoSolicitante() {
    if (isGetSecaoDefaultDadosDoSolicitanteLoading.value) return

    try {
      isGetSecaoDefaultDadosDoSolicitanteLoading.value = true
      secaoDefaultDadosDoSolicitante.value = await TicketService.getSecaoDefaultDadosDoSolicitante(ticketData.value.filaId)
    } finally {
      isGetSecaoDefaultDadosDoSolicitanteLoading.value = false
    }
  }

  async function getSecaoDefaultMensagens() {
    if (isGetSecaoDefaultMensagensLoading.value) return

    try {
      isGetSecaoDefaultMensagensLoading.value = true
      secaoDefaultMensagens.value = await TicketService.getSecaoDefaultMensagens(ticketData.value.filaId)
    } finally {
      isGetSecaoDefaultMensagensLoading.value = false
    }
  }

  async function getSecoesFormularios() {
    if (isGetSecoesFormulariosLoading.value) return

    try {
      isGetSecoesFormulariosLoading.value = true
      const data = await TicketService.getFormulariosTicket(ticketData.value.filaId, ticketData.value.tipoSolicitacaoId, unref(ticketId))
      secoesPersonalizadas.value = data.filter(s => s.idSecaoTipo === TiposDeSecoesEnum.Personalizada)
    } finally {
      isGetSecoesFormulariosLoading.value = false
    }
  }

  function resetMudancas() {
    ticketData.value = JSON.parse(JSON.stringify(ticketDataOriginal.value))
  }
  EventBus.$on('resetar-mudancas', resetMudancas)

  async function getTicketEAnexos() {
    await getTicketById()
    await getTicketMensagensAnexos()
  }

  async function handleTicketIdChange() {
    await getTicketById()
    await getSecaoDefaultMensagens()
    await getTicketMensagensAnexos()
    getSecaoDefaultDadosDoSolicitante()
    getSecoesFormularios()
  }

  function resetData() {
    ticketData.value = new GetTicketByIdModel()
    ticketDataOriginal.value = new GetTicketByIdModel()
    secoesPersonalizadas.value = []
    imagensEDocumentos.value = []
  }

  watch(
    ticketId,
    async () => {
      if (unref(ticketId)) {
        await handleTicketIdChange()
      } else {
        resetData()
      }
    },
    { immediate: true },
  )

  function receberDadosDoSolicitante(dados) {
    ticketData.value.solicitante.email = dados.email
    ticketData.value.solicitante.celular = dados.celular
    ticketData.value.solicitante.cpf = dados.cpf
    ticketData.value.solicitante.rg = dados.rg
    ticketData.value.solicitante.orgaoEmissor = dados.orgaoEmissor
    ticketData.value.solicitante.dataNascimento = dados.dataNascimento

    setTicketSelecionadoFoiAlterado(!areObjectPropertiesEqual(ticketData.value, ticketDataOriginal.value))
  }

  function atualizarTipoDeSolicitacao(tipoDeSolicitacaoId) {
    ticketData.value.tipoSolicitacaoId = tipoDeSolicitacaoId
    setTicketSelecionadoFoiAlterado(!areObjectPropertiesEqual(ticketData.value, ticketDataOriginal.value))
    getSecoesFormularios()
  }

  function receberRespostasFormularios(v) {
    v.forEach(elementoNovaResposta => {
      const elementoAntigaResposta = formulariosRespostas.value.find(it => it.idFormularioElemento === elementoNovaResposta.idFormularioElemento)

      if (elementoAntigaResposta) {
        const index = formulariosRespostas.value.indexOf(elementoAntigaResposta)
        formulariosRespostas.value.splice(index, 1, elementoNovaResposta)
      } else {
        formulariosRespostas.value.push(elementoNovaResposta)
      }
    })
  }

  async function resetTicketData() {
    await getTicketById()
    setTicketSelecionadoFoiAlterado(!areObjectPropertiesEqual(ticketData.value, ticketDataOriginal.value))
  }

  async function updateTicket() {
    try {
      isGetTicketLoading.value = true
      await TicketService.updateTicket(unref(ticketId), ticketData.value)
      await getTicketById()
    } finally {
      isGetTicketLoading.value = false
    }
  }

  EventBus.$on('fetch-ticket-anexos', getTicketMensagensAnexos)

  return {
    ticketData,
    imagensEDocumentos,
    formulariosRespostas,
    secaoDefaultDadosDoSolicitante,
    secoesPersonalizadas,
    secaoDefaultMensagens,
    ticketEstaFinalizado,
    ticketEstaResolvido,
    podeGravarAudio,
    podeResponderMensagem,
    expansionPanelsQueDevemAbrirPorDefault,
    getTicketById,
    getTicketEAnexos,
    receberDadosDoSolicitante,
    atualizarTipoDeSolicitacao,
    receberRespostasFormularios,
    resetTicketData,
    updateTicket,
  }
}
